import { z } from "zod";

/** Extensible provider identity. Adding an adapter never requires changing a shared enum. */
export const ProviderId = z.string().trim().min(1).max(64).regex(/^[a-z][a-z0-9-]*$/);
export type ProviderId = z.infer<typeof ProviderId>;

export const ProviderReadiness = z.enum(["ready", "unavailable", "degraded", "unknown"]);
export type ProviderReadiness = z.infer<typeof ProviderReadiness>;

export const ProviderReadinessReason = z.enum([
  "ready", "executable_missing", "authentication_required", "unsupported_version",
  "probe_timed_out", "probe_failed", "model_unavailable", "capability_unavailable",
  "worker_offline", "worker_not_reported",
]);
export type ProviderReadinessReason = z.infer<typeof ProviderReadinessReason>;

/** Cheapest first. `ultra` is Codex's top level: the model's highest effort
 * plus proactive sub-agents (`spawn_agent`), offered on GPT-6 Astra/Sol and
 * GPT-5.6 Sol/Terra as of codex-cli 0.156.0. */
export const ReasoningEffort = z.enum(["low", "medium", "high", "xhigh", "max", "ultra"]);
export type ReasoningEffort = z.infer<typeof ReasoningEffort>;

export const ProviderModel = z.object({
  id: z.string().trim().min(1).max(160),
  displayName: z.string().trim().min(1).max(160),
  /** The model a job naming none runs on — Codex's own default, or Rivets'
   * pick for Claude — and so the one every client preselects (see
   * `preselectedModelFor`). At most one per provider; a provider may mark
   * none, and then its CLI chooses. */
  isDefault: z.boolean().optional(),
  reasoningEfforts: z.array(ReasoningEffort).max(6).optional(),
  supportsFastMode: z.boolean().optional(),
});
export type ProviderModel = z.infer<typeof ProviderModel>;

export const ProviderFeatureSet = z.object({
  resumeSession: z.boolean(), turnSteering: z.boolean(), modelSelection: z.boolean(),
  reasoningEffort: z.boolean(), linkedHandoff: z.boolean(),
  fastMode: z.boolean(), planMode: z.boolean(), imageInput: z.boolean(),
});
export type ProviderFeatureSet = z.infer<typeof ProviderFeatureSet>;

/**
 * One of a provider subscription's rolling allowance windows, as the provider
 * itself reports it. Rivets never models a plan's size: it repeats the
 * provider's own percentage and reset time, or says nothing.
 */
export const ProviderUsageWindow = z.object({
  /** Share of the window's allowance already spent, 0–1. Providers disagree on
   * units — Claude sends a fraction, Codex whole percent — so the worker
   * normalizes and clamps before this ever reaches the wire. A provider past
   * its allowance reports 1; overage is a plan state, not a bigger number. */
  usedFraction: z.number().min(0).max(1),
  /** Unix ms at which this window's allowance is restored, when stated. */
  resetsAt: z.number().int().positive().optional(),
  /** Length of the rolling window in minutes, when stated. This is what makes
   * a window a "session" or a "week"; the bucket below is derived from it. */
  windowMinutes: z.number().int().positive().optional(),
});
export type ProviderUsageWindow = z.infer<typeof ProviderUsageWindow>;

/**
 * A window the provider meters for one model on top of the plan-wide pair —
 * Claude's separate weekly allowance for Fable, for instance. It is a window
 * like any other, plus the provider's own name for what it covers, shown
 * verbatim because only the provider knows which models it spans.
 */
export const ProviderScopedUsageWindow = z.object({
  /** The provider's name for the scope ("Fable"). */
  label: z.string().trim().min(1).max(60),
  usedFraction: z.number().min(0).max(1),
  resetsAt: z.number().int().positive().optional(),
  windowMinutes: z.number().int().positive().optional(),
});
export type ProviderScopedUsageWindow = z.infer<typeof ProviderScopedUsageWindow>;

/**
 * The provider account's remaining subscription allowance on this worker's
 * machine, as of `observedAt`.
 *
 * Both CLIs report two rolling windows — a short one (Claude's five-hour
 * window, Codex's primary) and a long one (Claude's seven-day window, Codex's
 * secondary) — so those are the two the contract names. A limit the provider
 * scopes to one model and names itself lands in `scoped`; anything else is
 * dropped rather than guessed at.
 *
 * Every field is optional in the honest sense. A worker that has not yet
 * observed a provider's usage reports no usage at all, and clients must say
 * "unknown" rather than draw an empty meter, which reads as "none used".
 */
export const ProviderAccountUsage = z.object({
  /** Unix ms when the worker observed these numbers. Usage is a snapshot, not
   * a live value: clients date it rather than implying it is current. */
  observedAt: z.number().int().nonnegative(),
  /** The short rolling window: Claude's five hours, Codex's primary. */
  session: ProviderUsageWindow.optional(),
  /** The long rolling window: Claude's seven days, Codex's secondary. */
  weekly: ProviderUsageWindow.optional(),
  /** Per-model windows the provider meters alongside the plan-wide ones, in
   * the order the provider lists them. Absent when it reports none. */
  scoped: z.array(ProviderScopedUsageWindow).max(8).optional(),
  /** The provider's own name for the subscription, when it states one
   * ("max", "prolite"). Displayed verbatim; never mapped to a Rivets tier. */
  planName: z.string().trim().min(1).max(60).optional(),
});
export type ProviderAccountUsage = z.infer<typeof ProviderAccountUsage>;

/** Longest window Rivets files under `session` rather than `weekly`. Claude's
 * short window is 5 h and Codex's primaries are 5 h or 7 d, so the split sits
 * at a day: anything longer is a weekly-style allowance. */
const SESSION_WINDOW_MAX_MINUTES = 24 * 60;

/** Files a provider-reported window under `session` or `weekly` by its length.
 * A window whose length the provider never stated cannot be filed at all —
 * the caller decides what to do with it, because only the caller knows which
 * of its provider's slots the window came from. */
export function providerUsageBucket(windowMinutes: number | undefined): "session" | "weekly" | undefined {
  if (windowMinutes === undefined || windowMinutes <= 0) return undefined;
  return windowMinutes <= SESSION_WINDOW_MAX_MINUTES ? "session" : "weekly";
}

export type ProviderUsageTone = "normal" | "warning" | "critical";

/**
 * One threshold pair for every surface that draws a subscription meter, so the
 * web app, the Mac app and the iPhone app cannot disagree about when a
 * subscription is "nearly gone". The native apps mirror these numbers; change
 * them here and there in the same commit.
 *
 * The thresholds are lower than the context-window meter's because the
 * consequence is worse: a full context window compacts, an exhausted weekly
 * allowance stops every task on the machine until the window rolls over.
 */
export function providerUsageTone(usedFraction: number | undefined): ProviderUsageTone {
  if (usedFraction === undefined) return "normal";
  if (usedFraction >= 0.9) return "critical";
  if (usedFraction >= 0.75) return "warning";
  return "normal";
}

/** The window a client should lead with: whichever of the two is closest to
 * being spent, because that is the one that will stop the next task. */
export function tightestUsageWindow(usage: ProviderAccountUsage | null | undefined): ProviderUsageWindow | undefined {
  const windows = [usage?.session, usage?.weekly, ...(usage?.scoped ?? [])].filter((window): window is ProviderUsageWindow => window !== undefined);
  return windows.sort((a, b) => b.usedFraction - a.usedFraction)[0];
}

export const ProviderStatus = z.object({
  providerId: ProviderId,
  displayName: z.string().trim().min(1).max(100),
  readiness: ProviderReadiness,
  reason: ProviderReadinessReason,
  detail: z.string().max(500).optional(),
  version: z.string().max(200).optional(),
  checkedAt: z.number().int().nonnegative(),
  features: ProviderFeatureSet,
  /** Efforts supported when the provider's default model is selected. */
  reasoningEfforts: z.array(ReasoningEffort).max(6),
  models: z.array(ProviderModel).max(100),
  /** Subscription allowance left on this worker's machine for this provider.
   * Absent until the worker has observed it once: both are read after the
   * first probe, so neither exists the moment a worker first registers, and a
   * provider that is signed out never reports any. */
  usage: ProviderAccountUsage.optional(),
});
export type ProviderStatus = z.infer<typeof ProviderStatus>;

/** Sole provider selection contract across REST, persistence, sockets and runtimes. */
export const ProviderSelection = z.object({
  providerId: ProviderId,
  model: z.string().trim().min(1).max(160).optional(),
  reasoningEffort: ReasoningEffort.optional(),
  fastMode: z.boolean().optional(),
  planMode: z.boolean().optional(),
});
export type ProviderSelection = z.infer<typeof ProviderSelection>;
/** Runtime-facing alias retained only as a source-code name, not a wire compatibility path. */
export const ProviderRunConfig = ProviderSelection.omit({ providerId: true });
export type ProviderRunConfig = z.infer<typeof ProviderRunConfig>;

export interface ProviderValidationResult { ok: boolean; reason?: ProviderReadinessReason; detail?: string }

export function validateProviderSelection(
  selection: ProviderSelection,
  statuses: readonly ProviderStatus[] | undefined,
): ProviderValidationResult {
  const status = statuses?.find((candidate) => candidate.providerId === selection.providerId);
  if (!status) return { ok: false, reason: "worker_not_reported", detail: "The worker has not reported this provider." };
  if (status.readiness !== "ready") return { ok: false, reason: status.reason, detail: status.detail };
  if (selection.model) {
    if (!status.features.modelSelection) return { ok: false, reason: "capability_unavailable", detail: "This provider does not support model selection." };
    if (!status.models.some((model) => model.id === selection.model)) return { ok: false, reason: "model_unavailable", detail: "The selected model is not available on this worker." };
  }
  if (selection.reasoningEffort) {
    if (!status.features.reasoningEffort) return { ok: false, reason: "capability_unavailable", detail: "This provider does not support reasoning effort." };
    const model = selection.model ? status.models.find((candidate) => candidate.id === selection.model) : undefined;
    const efforts = model?.reasoningEfforts ?? status.reasoningEfforts;
    if (!efforts.includes(selection.reasoningEffort)) return { ok: false, reason: "capability_unavailable", detail: "That reasoning effort is unavailable for the selected model." };
  }
  if (selection.fastMode) {
    if (!status.features.fastMode) return { ok: false, reason: "capability_unavailable", detail: "This provider does not support fast mode." };
    const model = selection.model
      ? status.models.find((candidate) => candidate.id === selection.model)
      : status.models.find((candidate) => candidate.isDefault);
    if (model && model.supportsFastMode !== true) return { ok: false, reason: "capability_unavailable", detail: "Fast mode is unavailable for the selected model." };
  }
  if (selection.planMode && !status.features.planMode) {
    return { ok: false, reason: "capability_unavailable", detail: "This provider does not support plan mode." };
  }
  return { ok: true };
}

/**
 * The model a new selection starts on, identical on every client: the
 * account's saved default while this worker still offers it, else the model
 * the worker marks `isDefault`. Undefined when the provider takes no model or
 * marks none — the selection then names no model and the provider's CLI picks,
 * which is also what a client should label ("Agent's default model").
 */
export function preselectedModelFor(
  status: ProviderStatus | undefined,
  preferredModel?: string | null,
): ProviderModel | undefined {
  if (!status?.features.modelSelection) return undefined;
  return (preferredModel ? status.models.find((model) => model.id === preferredModel) : undefined)
    ?? status.models.find((model) => model.isDefault);
}

/** Account defaults, structurally typed so `api.ts` can keep owning the schema
 * without this module importing it back. */
export interface ProviderDefaults {
  defaultProviderId: string | null;
  defaultModel: string | null;
  defaultReasoningEffort: ReasoningEffort | null;
}

/**
 * Narrows the account's saved defaults to what the offered providers actually
 * support right now. Every field is dropped independently, so a stale model
 * default never discards a still-valid agent default, and a preference is only
 * ever a preselection — it can never name something the worker cannot run.
 */
export function applyProviderDefaults(
  defaults: ProviderDefaults | null | undefined,
  statuses: readonly ProviderStatus[],
): { providerId?: ProviderId; model?: string; reasoningEffort?: ReasoningEffort } {
  if (!defaults) return {};
  const status = defaults.defaultProviderId
    ? statuses.find((candidate) => candidate.providerId === defaults.defaultProviderId)
    : undefined;
  if (!status) return {};
  const model = defaults.defaultModel && status.features.modelSelection
    ? status.models.find((candidate) => candidate.id === defaults.defaultModel)
    : undefined;
  const efforts = model?.reasoningEfforts
    ?? status.models.find((candidate) => candidate.isDefault)?.reasoningEfforts
    ?? status.reasoningEfforts;
  const reasoningEffort = defaults.defaultReasoningEffort && status.features.reasoningEffort
    && efforts.includes(defaults.defaultReasoningEffort)
    ? defaults.defaultReasoningEffort
    : undefined;
  return { providerId: status.providerId, model: model?.id, reasoningEffort };
}

/**
 * The release number in a provider's self-reported version, for a chip that
 * already names the provider. CLIs report "2.1.316 (Claude Code)" or
 * "codex-cli 0.163.0"; printed after the display name, either repeats it.
 * Falls back to the whole string when it has no dotted number in it.
 * `ProviderStatusInfo.versionNumber` in the native app is the same rule.
 */
export function providerVersionNumber(version: string | null | undefined): string | undefined {
  const trimmed = version?.trim();
  if (!trimmed) return undefined;
  return /\d+(?:\.\d+)+/.exec(trimmed)?.[0] ?? trimmed;
}

export function providerStatusesForWorker(
  capabilities: { providers: ProviderStatus[] } | null | undefined,
  workerOnline = true,
): ProviderStatus[] {
  const current = capabilities?.providers ?? [];
  if (workerOnline) return current;
  return current.map((status) => ({ ...status, readiness: "unavailable", reason: "worker_offline", detail: "This worker is offline." }));
}

/**
 * Model families that are the small, cheap tier of their provider, cheapest
 * tier first, matched case-insensitively against a model's id *and* display
 * name so a provider that reports `gpt-6-luna` and one that reports
 * "Haiku 4.5" both resolve. Ids are free text on the wire and Codex discovers
 * its catalogue at runtime, so this is deliberately a family heuristic rather
 * than a fixed list: an unrecognized model is simply never chosen as the cheap
 * one. The second tier is the mid-sized models, used only when a provider
 * offers no small tier at all.
 */
const MODEL_COST_TIERS: readonly (readonly RegExp[])[] = [
  [/haiku/, /luna/, /\bmini\b/, /nano/, /flash/, /\blite\b/, /\bsmall\b/, /instant/],
  [/sonnet/, /\bmedium\b/],
];

/** A model's long-context variant bills more per token for the same answer. */
const LARGE_CONTEXT_MODEL = /\[\d+m\]|-1m\b/i;

/**
 * The cheapest model a provider currently offers, for Rivets' own non-agentic
 * work — the assistant, which delegates rather than codes. Undefined when the
 * provider cannot take a model at all, or reports nothing recognizable, in
 * which case the caller leaves the provider on its own default.
 */
export function economyModelFor(status: ProviderStatus): ProviderModel | undefined {
  if (!status.features.modelSelection) return undefined;
  for (const tier of MODEL_COST_TIERS) {
    const matches = status.models.filter((model) =>
      tier.some((pattern) => pattern.test(`${model.id} ${model.displayName}`.toLowerCase())));
    if (matches.length === 0) continue;
    return matches.find((model) => !LARGE_CONTEXT_MODEL.test(model.id)) ?? matches[0];
  }
  return undefined;
}

/**
 * Model plus reasoning effort for a run that only has to read text and call
 * tools. Both fields are omitted unless the provider actually supports them,
 * so the result is always a valid `ProviderSelection` tail for `status`.
 */
export function economySelectionFor(status: ProviderStatus): { model?: string; reasoningEffort?: ReasoningEffort } {
  const model = economyModelFor(status);
  const efforts = model?.reasoningEfforts
    ?? status.models.find((candidate) => candidate.isDefault)?.reasoningEfforts
    ?? status.reasoningEfforts;
  const reasoningEffort = status.features.reasoningEffort
    ? ReasoningEffort.options.find((effort) => efforts.includes(effort))
    : undefined;
  return { ...(model ? { model: model.id } : {}), ...(reasoningEffort ? { reasoningEffort } : {}) };
}
